import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, CardActionArea,
  Tabs, Tab, Divider, Container, Breadcrumbs, Link, Paper,
  Avatar, Chip, Button, useTheme, alpha, List, ListItem, ListItemIcon, ListItemText, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Dashboard as DashboardIcon,
  Gavel as GavelIcon,
  Lightbulb as LightbulbIcon,
  TrendingUp as TrendingUpIcon,
  Business as BusinessIcon,
  ArrowForward as ArrowForwardIcon,
  People as PeopleIcon,
  MonetizationOn as MonetizationOnIcon,
  Science as ScienceIcon,
  Article as ArticleIcon,
  Check as CheckIcon
} from '@mui/icons-material';

// Import API service
import * as industryApi from '../services/industry';

// Import components
import ValueChainTable from '../components/valueChain/ValueChainTable';

const SubIndustryPage = () => {
  const { id, subId, subSubId } = useParams();
  const navigate = useNavigate();
  const [industry, setIndustry] = useState(null);
  const [subIndustry, setSubIndustry] = useState(null);
  const [parentSubIndustry, setParentSubIndustry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);
  const theme = useTheme();
  const isSubSubIndustry = !!subSubId;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch the parent industry with sub-industries
        const industryData = await industryApi.getIndustryById(id, {
          includeSubIndustries: true,
          includeValueChain: true
        });

        if (industryData) {
          setIndustry(industryData);

          if (isSubSubIndustry) {
            // For sub-sub-industries, first fetch the parent sub-industry
            const parentDetails = await industryApi.getSubIndustryById(id, subId, {
              includeValueChain: true,
              includeSupportEcosystem: true
            });

            if (parentDetails) {
              setParentSubIndustry(parentDetails);

              // Then find the sub-sub-industry in the parent's childSubIndustries
              if (parentDetails.childSubIndustries) {
                const foundSubSubIndustry = parentDetails.childSubIndustries.find(child => child.id === subSubId);

                if (foundSubSubIndustry) {
                  // Fetch full sub-sub-industry details
                  const subSubDetails = await industryApi.getSubIndustryById(id, subSubId, {
                    includeValueChain: true,
                    includeSupportEcosystem: true
                  });
                  setSubIndustry(subSubDetails || foundSubSubIndustry);
                } else {
                  setError('Sub-sub-industry not found');
                }
              } else {
                setError('No child sub-industries found');
              }
            } else {
              setError('Parent sub-industry not found');
            }
          } else {
            // For regular sub-industries
            if (industryData.subIndustries) {
              const foundSubIndustry = industryData.subIndustries.find(sub => sub.id === subId);

              if (foundSubIndustry) {
                // Fetch full sub-industry details with value chain
                const subIndustryDetails = await industryApi.getSubIndustryById(id, subId, {
                  includeValueChain: true,
                  includeSupportEcosystem: true
                });
                setSubIndustry(subIndustryDetails || foundSubIndustry);
              } else {
                setError('Sub-industry not found');
              }
            } else {
              setError('No sub-industries found');
            }
          }
        } else {
          setError('Industry not found');
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load sub-industry data');
      } finally {
        setLoading(false);
      }
    };

    if (id && (subId || subSubId)) {
      fetchData();
    }

    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }, [id, subId, subSubId, isSubSubIndustry]);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleRelatedSubIndustryClick = (relatedSubId) => {
    navigate(`/industry/${id}/sub/${relatedSubId}`);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '70vh'
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>Loading sub-industry data...</Typography>
        </Box>
      </Box>
    );
  }

  if (error || !industry || !subIndustry) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h5" color="error" gutterBottom>
            {error || 'Sub-industry not found'}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate(id ? `/industry/${id}` : '/')}
            sx={{ mt: 2 }}
          >
            {id ? `Back to Industry` : 'Back to Home'}
          </Button>
        </Box>
      </Container>
    );
  }

  const getTabIcon = (index) => {
    switch (index) {
      case 0: return <DashboardIcon />;
      case 1: return <GavelIcon />;
      case 2: return <LightbulbIcon />;
      case 3: return <TrendingUpIcon />;
      default: return null;
    }
  };

  // Mapping tab index to tab data property
  const getTabContent = (tabIndex) => {
    if (!subIndustry || !subIndustry.tabs) return null;

    switch (tabIndex) {
      case 0: return subIndustry.tabs.marketOverview; // Market Overview tab
      case 1: return subIndustry.tabs.legalInfrastructure; // Legal & Infrastructure tab
      case 2: return null; // Opportunities & Incentives tab (not in provided data)
      case 3: return subIndustry.tabs.trends; // Trends tab
      default: return null;
    }
  };

  // Helper function to render list items with bullet points
  const renderBulletList = (items) => {
    if (!items || !Array.isArray(items)) return null;

    return (
      <List sx={{ mt: 2 }}>
        {items.map((item, index) => (
          <ListItem key={index} sx={{ py: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <CheckIcon color="primary" fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>
    );
  };

  // Helper function to render a section with a title and bullet points
  const renderSection = (title, items) => {
    if (!items || !Array.isArray(items) || items.length === 0) return null;

    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" fontWeight={600} color="primary.main" gutterBottom>
          {title}
        </Typography>
        {renderBulletList(items)}
      </Box>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs with improved styling */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" color="primary" />}
        aria-label="breadcrumb"
        sx={{
          mb: 4,
          '& .MuiBreadcrumbs-li': {
            display: 'flex',
            alignItems: 'center'
          }
        }}
      >
        <Link
          color="inherit"
          href="/"
          onClick={(e) => { e.preventDefault(); navigate('/'); }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            '&:hover': {
              color: theme.palette.primary.main
            }
          }}
        >
          Home
        </Link>
        <Link
          color="inherit"
          href={`/industry/${industry.id}`}
          onClick={(e) => { e.preventDefault(); navigate(`/industry/${industry.id}`); }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            '&:hover': {
              color: theme.palette.primary.main
            }
          }}
        >
          {industry.name}
        </Link>
        <Typography color="text.primary" fontWeight="medium">{subIndustry.name}</Typography>
      </Breadcrumbs>

      {/* Hero section with gradient background */}
      <Paper
        elevation={0}
        sx={{
          mb: 6,
          p: { xs: 3, md: 5 },
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.1)})`,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '40%',
            height: '100%',
            opacity: 0.1,
            background: `radial-gradient(circle, ${theme.palette.secondary.main} 0%, transparent 70%)`,
            transform: 'translateX(30%)'
          }}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 3,
            position: 'relative',
            zIndex: 1
          }}
        >
          <Box>
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}
            >
              {subIndustry.name}
            </Typography>

            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{
                maxWidth: '800px',
                lineHeight: 1.6,
                mb: 2
              }}
            >
              {subIndustry.description}
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
              <Chip
                label={`${industry.name} Segment`}
                color="secondary"
                size="medium"
                sx={{ borderRadius: 1, fontWeight: 500 }}
              />
              <Chip
                label="Market Analysis"
                variant="outlined"
                size="medium"
                sx={{ borderRadius: 1, fontWeight: 500 }}
              />
              <Chip
                label="Specialized Value Chain"
                variant="outlined"
                size="medium"
                sx={{ borderRadius: 1, fontWeight: 500 }}
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Sub-Sub-Industry Special Format (Level 3) - Redesigned */}
      {isSubSubIndustry && (
        <Box sx={{ mb: 6 }}>
          {/* One-liner - Hero Section */}
          {subIndustry.oneLiner && (
            <Paper
              elevation={0}
              sx={{
                mb: 4,
                p: 4,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                }
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontStyle: 'italic',
                  color: theme.palette.primary.dark,
                  fontWeight: 600,
                  textAlign: 'center',
                  letterSpacing: 0.5
                }}
              >
                "{subIndustry.oneLiner}"
              </Typography>
            </Paper>
          )}

          {/* Market Summary - Enhanced Design */}
          {subIndustry.summary && (
            <Paper
              elevation={2}
              sx={{
                mb: 5,
                borderRadius: 3,
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  p: 2.5,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  color: 'white'
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <ArticleIcon />
                  Market Summary
                </Typography>
              </Box>
              <Box sx={{ p: 4 }}>
                <Typography
                  variant="body1"
                  sx={{
                    lineHeight: 2,
                    fontSize: '1.05rem',
                    color: theme.palette.text.primary
                  }}
                >
                  {subIndustry.summary}
                </Typography>
              </Box>
            </Paper>
          )}

          {/* Trends, Top Opportunities, Technology - Enhanced Cards */}
          <Grid container spacing={3} sx={{ mb: 5 }}>
            {/* Trends */}
            {subIndustry.trends && subIndustry.trends.length > 0 && (
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={3}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    overflow: 'hidden',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[8]
                    }
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
                      color: 'white'
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrendingUpIcon />
                      Trends
                    </Typography>
                  </Box>
                  <Box sx={{ p: 3 }}>
                    <List sx={{ p: 0 }}>
                      {subIndustry.trends.map((trend, idx) => (
                        <ListItem
                          key={idx}
                          sx={{
                            px: 0,
                            py: 1,
                            alignItems: 'flex-start'
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                            <CheckIcon sx={{ color: theme.palette.info.main, fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={trend}
                            primaryTypographyProps={{
                              variant: 'body2',
                              sx: { lineHeight: 1.6 }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Paper>
              </Grid>
            )}

            {/* Top Opportunities */}
            {subIndustry.topOpportunities && subIndustry.topOpportunities.length > 0 && (
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={3}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    overflow: 'hidden',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[8]
                    }
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                      color: 'white'
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LightbulbIcon />
                      Top Opportunities
                    </Typography>
                  </Box>
                  <Box sx={{ p: 3 }}>
                    <List sx={{ p: 0 }}>
                      {subIndustry.topOpportunities.map((opp, idx) => (
                        <ListItem
                          key={idx}
                          sx={{
                            px: 0,
                            py: 1,
                            alignItems: 'flex-start'
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                            <CheckIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={opp}
                            primaryTypographyProps={{
                              variant: 'body2',
                              sx: { lineHeight: 1.6 }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Paper>
              </Grid>
            )}

            {/* Technology */}
            {subIndustry.technology && subIndustry.technology.length > 0 && (
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={3}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    overflow: 'hidden',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[8]
                    }
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                      color: 'white'
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ScienceIcon />
                      Technology
                    </Typography>
                  </Box>
                  <Box sx={{ p: 3 }}>
                    <List sx={{ p: 0 }}>
                      {subIndustry.technology.map((tech, idx) => (
                        <ListItem
                          key={idx}
                          sx={{
                            px: 0,
                            py: 1,
                            alignItems: 'flex-start'
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                            <CheckIcon sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={tech}
                            primaryTypographyProps={{
                              variant: 'body2',
                              sx: { lineHeight: 1.6 }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>

          {/* Opportunities Table - Enhanced Design */}
          {subIndustry.opportunitiesTable && subIndustry.opportunitiesTable.length > 0 && (
            <Paper
              elevation={3}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                mb: 4
              }}
            >
              <Box
                sx={{
                  p: 2.5,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  color: 'white'
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <MonetizationOnIcon />
                  Opportunities
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.95rem',
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                          color: theme.palette.primary.dark,
                          borderBottom: `2px solid ${theme.palette.primary.main}`
                        }}
                      >
                        Opportunity
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.95rem',
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                          color: theme.palette.primary.dark,
                          borderBottom: `2px solid ${theme.palette.primary.main}`
                        }}
                      >
                        Brief
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.95rem',
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                          color: theme.palette.primary.dark,
                          borderBottom: `2px solid ${theme.palette.primary.main}`
                        }}
                      >
                        Products/Services
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subIndustry.opportunitiesTable.map((row, idx) => (
                      <TableRow
                        key={idx}
                        hover
                        sx={{
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.04)
                          },
                          '&:nth-of-type(odd)': {
                            backgroundColor: alpha(theme.palette.grey[500], 0.02)
                          }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>{row.opportunity}</TableCell>
                        <TableCell>{row.brief}</TableCell>
                        <TableCell>{row.products_services}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      )}

      {/* Market Summary Section - Show only for regular sub-industries */}
      {!isSubSubIndustry && (
        <Paper
          elevation={2}
          sx={{
            mb: 6,
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            background: `linear-gradient(to right, ${alpha(theme.palette.secondary.main, 0.05)}, ${alpha(theme.palette.background.paper, 1)} 70%)`,
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`
          }}
        >
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: 600,
              mb: 2,
              position: 'relative',
              display: 'inline-block',
              '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -8,
                left: 0,
                width: '50%',
                height: 4,
                borderRadius: 2,
                backgroundColor: theme.palette.secondary.main
              }
            }}
          >
            Market Summary
          </Typography>

          <Typography
            variant="body1"
            sx={{
              lineHeight: 1.8,
              color: theme.palette.text.primary,
              mt: 3
            }}
          >
            {subIndustry.summary}
          </Typography>
        </Paper>
      )}

      {/* Enhanced tabs with actual data content - Hide for sub-sub-industries */}
      {!isSubSubIndustry && (<Box sx={{ width: '100%', mt: 6, mb: 8 }}>
        <Paper
          elevation={2}
          sx={{
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          <Tabs
            value={tab}
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: `1px solid ${theme.palette.divider}`,
              '& .MuiTab-root': {
                minHeight: 64,
                px: 3
              }
            }}
          >
            {['Market Overview', 'Legal & Infrastructure', 'Opportunities & Incentives', 'Trends'].map((label, index) => (
              <Tab
                key={label}
                label={label}
                icon={getTabIcon(index)}
                iconPosition="start"
                sx={{
                  fontWeight: 500,
                  textTransform: 'none',
                  fontSize: '1rem'
                }}
              />
            ))}
          </Tabs>

          <Box sx={{ p: 4 }}>
            {/* Market Overview Tab */}
            {tab === 0 && (
              <Box>
                <Typography variant="h5" gutterBottom fontWeight={600}>
                  Market Overview
                </Typography>

                {subIndustry.tabs && subIndustry.tabs.marketOverview && (
                  <>
                    {/* Market Potential */}
                    {renderSection('Market Potential', subIndustry.tabs.marketOverview.marketPotential)}

                    {/* Critical Gaps */}
                    {renderSection('Critical Gaps', subIndustry.tabs.marketOverview.criticalGaps)}

                    {/* Critical Strengths */}
                    {renderSection('Critical Strengths', subIndustry.tabs.marketOverview.criticalStrengths)}

                    {/* Gaps Blocking Growth */}
                    {renderSection('Gaps Blocking Growth', subIndustry.tabs.marketOverview.gapsBlockingGrowth)}

                    {/* Investment Shortfalls */}
                    {renderSection('Investment Shortfalls', subIndustry.tabs.marketOverview.investmentShortfalls)}

                    {/* Bottom Line */}
                    {subIndustry.tabs.marketOverview.bottomLine && (
                      <Box sx={{ mt: 4, p: 3, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          Bottom Line
                        </Typography>
                        <Typography variant="body1" paragraph>
                          {subIndustry.tabs.marketOverview.bottomLine.text}
                        </Typography>
                        {renderBulletList(subIndustry.tabs.marketOverview.bottomLine.points)}
                      </Box>
                    )}

                    {/* National Strategy */}
                    {subIndustry.tabs.marketOverview.nationalStrategy && (
                      <Box sx={{ mt: 4, p: 3, bgcolor: alpha(theme.palette.secondary.main, 0.05), borderRadius: 2, border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}` }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom color="secondary.main">
                          {subIndustry.tabs.marketOverview.nationalStrategy.title}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Published: {subIndustry.tabs.marketOverview.nationalStrategy.published}
                        </Typography>

                        <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
                          Vision
                        </Typography>
                        <Typography variant="body1" paragraph>
                          {subIndustry.tabs.marketOverview.nationalStrategy.vision}
                        </Typography>

                        <Typography variant="subtitle1" fontWeight={600}>
                          Focus Areas
                        </Typography>
                        {renderBulletList(subIndustry.tabs.marketOverview.nationalStrategy.focusAreas)}

                        <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
                          Pillars
                        </Typography>
                        {renderBulletList(subIndustry.tabs.marketOverview.nationalStrategy.pillars)}

                        <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
                          Why It Matters
                        </Typography>
                        {renderBulletList(subIndustry.tabs.marketOverview.nationalStrategy.whyItMatters)}

                        <Button
                          variant="outlined"
                          color="secondary"
                          sx={{ mt: 2 }}
                          href={subIndustry.tabs.marketOverview.nationalStrategy.documentLink}
                          target="_blank"
                        >
                          View Full Strategy Document
                        </Button>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )}

            {/* Legal & Infrastructure Tab */}
            {tab === 1 && (
              <Box>
                <Typography variant="h5" gutterBottom fontWeight={600}>
                  Legal & Infrastructure
                </Typography>

                {subIndustry.tabs && subIndustry.tabs.legalInfrastructure && (
                  <Grid container spacing={4}>
                    {/* Legal Section */}
                    <Grid item xs={12} md={6}>
                      <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom color="primary.main">
                          Legal Framework
                        </Typography>

                        {/* Regulatory Links */}
                        {subIndustry.tabs.legalInfrastructure.legal && subIndustry.tabs.legalInfrastructure.legal.regulatoryLinks && (
                          <Box sx={{ mt: 2 }}>
                            {subIndustry.tabs.legalInfrastructure.legal.regulatoryLinks.map((section, idx) => (
                              <Box key={idx} sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" fontWeight={600}>{section.title}</Typography>
                                <List dense>
                                  {section.items.map((item, itemIdx) => (
                                    <ListItem key={itemIdx} sx={{ px: 0 }}>
                                      <ListItemIcon sx={{ minWidth: 32 }}>
                                        <CheckIcon color="primary" fontSize="small" />
                                      </ListItemIcon>
                                      <ListItemText
                                        primary={
                                          <Link href={item.link} target="_blank" color="primary">
                                            {item.name}
                                          </Link>
                                        }
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                              </Box>
                            ))}
                          </Box>
                        )}

                        {/* SFDA ML4 Section */}
                        {subIndustry.tabs.legalInfrastructure.legal.sfdaML4 && (
                          <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                              {subIndustry.tabs.legalInfrastructure.legal.sfdaML4.title}
                            </Typography>
                            <Typography variant="body2" paragraph>
                              {subIndustry.tabs.legalInfrastructure.legal.sfdaML4.description}
                            </Typography>
                            {renderBulletList(subIndustry.tabs.legalInfrastructure.legal.sfdaML4.impact)}
                          </Box>
                        )}

                        {/* Notes */}
                        {renderSection('Notes', subIndustry.tabs.legalInfrastructure.legal.notes)}

                        {/* Foundations */}
                        {renderSection('Foundations', subIndustry.tabs.legalInfrastructure.legal.foundations)}
                      </Paper>
                    </Grid>

                    {/* Infrastructure Section */}
                    <Grid item xs={12} md={6}>
                      <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.secondary.main, 0.05), borderRadius: 2 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom color="secondary.main">
                          Infrastructure
                        </Typography>

                        {/* Physical Infrastructure */}
                        {renderSection('Physical Infrastructure', subIndustry.tabs.legalInfrastructure.infrastructure.physical)}

                        {/* Key Conflict */}
                        {subIndustry.tabs.legalInfrastructure.infrastructure.keyConflict && (
                          <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle1" fontWeight={600} color="error.main">
                              Key Conflict
                            </Typography>
                            <Typography variant="body1" color="error.main" sx={{ fontStyle: 'italic', mt: 1 }}>
                              {subIndustry.tabs.legalInfrastructure.infrastructure.keyConflict}
                            </Typography>
                          </Box>
                        )}

                        {/* Digital Infrastructure */}
                        {renderSection('Digital Infrastructure', subIndustry.tabs.legalInfrastructure.infrastructure.digital)}

                        {/* Priorities */}
                        {subIndustry.tabs.legalInfrastructure.infrastructure.priorities && (
                          <Box sx={{ mt: 4, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}` }}>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                              Priorities
                            </Typography>
                            <Typography variant="body2" paragraph>
                              {subIndustry.tabs.legalInfrastructure.infrastructure.priorities.intro}
                            </Typography>
                            {renderBulletList(subIndustry.tabs.legalInfrastructure.infrastructure.priorities.items)}
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  </Grid>
                )}
              </Box>
            )}

            {/* Opportunities & Incentives Tab */}
            {tab === 2 && (
              <Box>
                <Typography variant="h5" gutterBottom fontWeight={600}>
                  Opportunities & Incentives
                </Typography>

                <Typography variant="body1" sx={{ maxWidth: '800px', lineHeight: 1.7, mb: 3 }}>
                  The Kingdom offers numerous incentives for {subIndustry.name} investors, including tax benefits, subsidized
                  land and utilities, and financial support through institutions like the Saudi Industrial Development Fund.
                  Opportunities exist in localization of manufacturing for essential products, innovation, and development
                  aligned with national priorities.
                </Typography>

                {subIndustry.tabs && subIndustry.tabs.incentives ? (
                  <>
                    {/* Financial Incentives Section */}
                    {subIndustry.tabs.incentives.content.sections.map((section, sectionIndex) => (
                      <Paper
                        key={sectionIndex}
                        elevation={1}
                        sx={{
                          mt: 3,
                          mb: 4,
                          p: 3,
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                        }}
                      >
                        <Typography
                          variant="h6"
                          fontWeight={600}
                          gutterBottom
                          color="primary.main"
                          sx={{
                            pb: 1,
                            borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                          }}
                        >
                          {section.title}
                        </Typography>

                        {/* If the section has subsections */}
                        {section.items ? (
                          section.items.map((item, itemIndex) => (
                            <Box key={itemIndex} sx={{ mt: 2 }}>
                              {item.subtitle && (
                                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                  {item.subtitle}
                                </Typography>
                              )}
                              <List dense>
                                {item.points && item.points.map((point, pointIndex) => (
                                  <ListItem key={pointIndex} sx={{ py: 0.5 }}>
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                      <CheckIcon color="primary" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={
                                        <Typography variant="body2" dangerouslySetInnerHTML={{ __html: point }} />
                                      }
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          ))
                        ) : (
                          /* If the section has direct points */
                          <List dense>
                            {section.points && section.points.map((point, pointIndex) => (
                              <ListItem key={pointIndex} sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <CheckIcon color="primary" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <Typography variant="body2" dangerouslySetInnerHTML={{ __html: point }} />
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        )}
                      </Paper>
                    ))}

                    {/* Investment Opportunities Section */}
                    <Box sx={{ mt: 4, mb: 3 }}>
                      <Paper
                        elevation={2}
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          background: `linear-gradient(to right, ${alpha(theme.palette.success.light, 0.1)}, ${alpha(theme.palette.background.paper, 1)})`,
                          border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                        }}
                      >
                        <Typography variant="h6" fontWeight={600} gutterBottom color="success.dark">
                          Investment Opportunities
                        </Typography>

                        <Typography variant="body1" paragraph>
                          Connect with startups seeking investment and investors looking for opportunities in the {subIndustry.name} field.
                        </Typography>

                        <Grid container spacing={3} sx={{ mt: 1 }}>
                          <Grid item xs={12} md={6}>
                            <Card sx={{ height: '100%', borderRadius: 2, border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
                              <CardActionArea
                                sx={{ height: '100%', p: 2 }}
                                onClick={() => navigate(`/marketplace?filter=startups&industry=${id}&subIndustry=${subId}`)}
                              >
                                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <LightbulbIcon color="success" sx={{ mr: 1 }} />
                                    <Typography variant="h6" fontWeight={600}>Startups Seeking Investment</Typography>
                                  </Box>
                                  <Typography variant="body2" color="text.secondary" paragraph>
                                    Browse {subIndustry.name} startups actively seeking funding at various stages.
                                  </Typography>
                                  <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                    <Typography variant="button" color="success.main" sx={{ mr: 0.5 }}>
                                      Find Startups
                                    </Typography>
                                    <ArrowForwardIcon fontSize="small" color="success" />
                                  </Box>
                                </Box>
                              </CardActionArea>
                            </Card>
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <Card sx={{ height: '100%', borderRadius: 2, border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
                              <CardActionArea
                                sx={{ height: '100%', p: 2 }}
                                onClick={() => navigate(`/marketplace?filter=investors&industry=${id}&subIndustry=${subId}`)}
                              >
                                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <MonetizationOnIcon color="success" sx={{ mr: 1 }} />
                                    <Typography variant="h6" fontWeight={600}>Investors Seeking Opportunities</Typography>
                                  </Box>
                                  <Typography variant="body2" color="text.secondary" paragraph>
                                    Connect with investors interested in {subIndustry.name} ventures and products.
                                  </Typography>
                                  <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                    <Typography variant="button" color="success.main" sx={{ mr: 0.5 }}>
                                      Find Investors
                                    </Typography>
                                    <ArrowForwardIcon fontSize="small" color="success" />
                                  </Box>
                                </Box>
                              </CardActionArea>
                            </Card>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Box>
                  </>
                ) : (
                  <Paper
                    elevation={1}
                    sx={{
                      mt: 3,
                      p: 3,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.warning.light, 0.1),
                      border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Detailed opportunities and incentives data will be available soon.
                    </Typography>
                  </Paper>
                )}
              </Box>
            )}

            {/* Trends Tab */}
            {tab === 3 && (
              <Box>
                <Typography variant="h5" gutterBottom fontWeight={600}>
                  Trends
                </Typography>

                {subIndustry.tabs && subIndustry.tabs.trends && (
                  <>
                    {/* Trends List */}
                    {subIndustry.tabs.trends.trendsList && (
                      <Grid container spacing={3} sx={{ mt: 1 }}>
                        {subIndustry.tabs.trends.trendsList.map((trend, index) => (
                          <Grid item xs={12} md={6} key={index}>
                            <Paper
                              elevation={1}
                              sx={{
                                p: 3,
                                borderRadius: 2,
                                height: '100%',
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                              }}
                            >
                              <Typography
                                variant="h6"
                                gutterBottom
                                sx={{
                                  fontWeight: 600,
                                  color: theme.palette.primary.main,
                                  pb: 1,
                                  borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                                }}
                              >
                                {trend.name}
                              </Typography>

                              {trend.points && trend.points.map((point, pointIdx) => {
                                // Check if the point contains a colon to split into title and content
                                const hasSplit = point.includes(': ');
                                const [title, content] = hasSplit ? point.split(': ') : [null, point];

                                return (
                                  <Box key={pointIdx} sx={{ mt: 2 }}>
                                    {hasSplit && (
                                      <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                                        {title}:
                                      </Typography>
                                    )}
                                    <Typography variant="body2" color="text.secondary">
                                      {content}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    )}

                    {/* Where To Start */}
                    {subIndustry.tabs.trends.whereToStart && (
                      <Box sx={{ mt: 4, p: 3, bgcolor: alpha(theme.palette.success.light, 0.1), borderRadius: 2 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom color="success.dark">
                          Where To Start
                        </Typography>
                        {renderBulletList(subIndustry.tabs.trends.whereToStart)}
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
      )}

      {/* Value Chain section with enhanced UI - Hide for sub-sub-industries */}
      {!isSubSubIndustry && ((subIndustry.childSubIndustries && subIndustry.childSubIndustries.length > 0) || (subIndustry.valueChainStages && subIndustry.valueChainStages.length > 0)) ? (
        <Paper
          elevation={1}
          sx={{
            mt: 4,
            mb: 8,
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              p: 3,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: 'white'
            }}
          >
            <Typography variant="h4" fontWeight={600}>
              Value Chain
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
              Model of the Value Chain. Each name is a separate page with data.
            </Typography>
          </Box>

          <Box p={3}>
            <ValueChainTable
              valueChainId={subId}
              valueChainData={{
                stages: (subIndustry.childSubIndustries && subIndustry.childSubIndustries.length > 0)
                  ? subIndustry.childSubIndustries.map((child, index) => ({
                      name: child.name,
                      title: child.name,
                      content: child.name,
                      summary: child.summary,
                      order: child.displayOrder || index
                    }))
                  : (subIndustry.valueChainStages || []).sort((a, b) => a.order - b.order)
              }}
              industryId={id}
              subIndustryId={subId}
              subIndustries={subIndustry.childSubIndustries || []}
            />
          </Box>
        </Paper>
      ) : (
        <Paper
          elevation={1}
          sx={{
            mt: 4,
            mb: 8,
            p: 4,
            borderRadius: 3,
            background: alpha(theme.palette.warning.light, 0.1),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`
          }}
        >
          <Typography variant="h5" color="warning.dark" fontWeight={600} gutterBottom>
            Value Chain Data Not Available
          </Typography>
          <Typography variant="body1" color="text.secondary">
            A specialized value chain for {subIndustry.name} is currently being developed.
            Please check the main {industry.name} value chain for reference.
          </Typography>
          <Button
            variant="outlined"
            color="warning"
            sx={{ mt: 2, borderRadius: 2, textTransform: 'none' }}
            onClick={() => navigate(`/industry/${id}`)}
          >
            View Industry Value Chain
          </Button>
        </Paper>
      )}

      {/* Support Ecosystem Section */}
      <Paper
        elevation={1}
        sx={{
          mt: 4,
          mb: 8,
          borderRadius: 3,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            p: 3,
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            color: 'white'
          }}
        >
          <Typography variant="h4" fontWeight={600}>
            Support Ecosystem
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
            Critical supporting elements for successful industry development
          </Typography>
          {subIndustry.supportEcosystem && subIndustry.supportEcosystem.summary && (
            <Typography variant="subtitle1" sx={{ mt: 2, opacity: 0.95 }}>
              {subIndustry.supportEcosystem.summary}
            </Typography>
          )}
        </Box>

        <Box p={3}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <Box
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  background: alpha(theme.palette.background.paper, 0.7),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight={600}
                  color="primary.main"
                  align="center"
                  sx={{
                    position: 'relative',
                    pb: 2,
                    '&:after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '30%',
                      width: '40%',
                      height: 3,
                      borderRadius: 1.5,
                      backgroundColor: theme.palette.primary.main
                    }
                  }}
                >
                  SUPPORT ECOSYSTEM
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  sx={{ mt: 2 }}
                >
                  Essential infrastructure and resources that enable the industry value chain to operate effectively
                </Typography>

                {subIndustry.supportEcosystem && subIndustry.supportEcosystem.productsServicesNeeded && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" fontWeight={600} color="primary.main">
                      Critical Needs:
                    </Typography>
                    <List dense disablePadding>
                      {subIndustry.supportEcosystem.productsServicesNeeded.map((item, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <CheckIcon color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)'
                  }
                }}
              >
                <CardActionArea
                  sx={{
                    height: '100%',
                    borderTop: `4px solid ${theme.palette.info.main}`
                  }}
                  onClick={() => navigate(`/support/talent?industry=${id}&subIndustry=${subId}`)}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PeopleIcon color="info" sx={{ mr: 1.5, fontSize: 28 }} />
                      <Typography variant="h6" fontWeight={600} color="info.main">
                        Talent
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1.5 }} />

                    {subIndustry.supportEcosystem && subIndustry.supportEcosystem.talent ? (
                      <>
                        {subIndustry.supportEcosystem.talent.currentWorkforceGaps &&
                          subIndustry.supportEcosystem.talent.currentWorkforceGaps.criticalShortages && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                Critical Shortages:
                              </Typography>
                              <List dense disablePadding>
                                {subIndustry.supportEcosystem.talent.currentWorkforceGaps.criticalShortages.slice(0, 2).map((item, index) => (
                                  <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                    <ListItemIcon sx={{ minWidth: 28 }}>
                                      <CheckIcon color="info" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={
                                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                          {item.split('(')[0]}
                                        </Typography>
                                      }
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          )}

                        {subIndustry.supportEcosystem.talent.universityPrograms && (
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                              Key Universities:
                            </Typography>
                            <List dense disablePadding>
                              {subIndustry.supportEcosystem.talent.universityPrograms.slice(0, 2).map((uni, index) => (
                                <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                  <ListItemIcon sx={{ minWidth: 28 }}>
                                    <CheckIcon color="info" fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText primary={uni.name} />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}
                      </>
                    ) : (
                      <List dense disablePadding>
                        {['University Partnerships', 'Technical Training Programs', 'R&D Centers', 'Specialized Certifications'].map((item, index) => (
                          <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <CheckIcon color="info" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={item} />
                          </ListItem>
                        ))}
                      </List>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 2 }}>
                      <Typography variant="button" color="info.main" sx={{ display: 'flex', alignItems: 'center' }}>
                        Explore
                        <ArrowForwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                      </Typography>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)'
                  }
                }}
              >
                <CardActionArea
                  sx={{
                    height: '100%',
                    borderTop: `4px solid ${theme.palette.success.main}`
                  }}
                  onClick={() => navigate(`/support/funding?industry=${id}&subIndustry=${subId}`)}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <MonetizationOnIcon color="success" sx={{ mr: 1.5, fontSize: 28 }} />
                      <Typography variant="h6" fontWeight={600} color="success.main">
                        Funding
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1.5 }} />

                    {subIndustry.supportEcosystem && subIndustry.supportEcosystem.funding ? (
                      <>
                        {subIndustry.supportEcosystem.funding.investmentLandscape &&
                          subIndustry.supportEcosystem.funding.investmentLandscape.keyMetrics && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                Key Metrics:
                              </Typography>
                              <List dense disablePadding>
                                {subIndustry.supportEcosystem.funding.investmentLandscape.keyMetrics.slice(0, 2).map((item, index) => (
                                  <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                    <ListItemIcon sx={{ minWidth: 28 }}>
                                      <CheckIcon color="success" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={
                                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                          {item}
                                        </Typography>
                                      }
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          )}

                        {subIndustry.supportEcosystem.funding.fundingSources && (
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                              Key Sources:
                            </Typography>
                            <List dense disablePadding>
                              {subIndustry.supportEcosystem.funding.fundingSources.slice(0, 1).map((source, index) => (
                                source.entities.slice(0, 2).map((entity, entityIndex) => (
                                  <ListItem key={`${index}-${entityIndex}`} sx={{ px: 0, py: 0.5 }}>
                                    <ListItemIcon sx={{ minWidth: 28 }}>
                                      <CheckIcon color="success" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={
                                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical' }}>
                                          {entity.split(':')[0]}
                                        </Typography>
                                      }
                                    />
                                  </ListItem>
                                ))
                              ))}
                            </List>
                          </Box>
                        )}
                      </>
                    ) : (
                      <List dense disablePadding>
                        {['Investment Funds', 'Government Incentives', 'Venture Capital', 'Development Banks'].map((item, index) => (
                          <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <CheckIcon color="success" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={item} />
                          </ListItem>
                        ))}
                      </List>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 2 }}>
                      <Typography variant="button" color="success.main" sx={{ display: 'flex', alignItems: 'center' }}>
                        Explore
                        <ArrowForwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                      </Typography>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  borderRadius: 2,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)'
                  }
                }}
              >
                <CardActionArea
                  sx={{
                    height: '100%',
                    borderTop: `4px solid ${theme.palette.secondary.main}`
                  }}
                  onClick={() => navigate(`/support/supply-chain?industry=${id}&subIndustry=${subId}`)}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <BusinessIcon color="secondary" sx={{ mr: 1.5, fontSize: 28 }} />
                      <Typography variant="h6" fontWeight={600} color="secondary.main">
                        Supply Chain
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1.5 }} />

                    {subIndustry.supportEcosystem && subIndustry.supportEcosystem.supplyChain ? (
                      <>
                        {subIndustry.supportEcosystem.supplyChain.criticalGaps && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                              Critical Gaps:
                            </Typography>
                            <List dense disablePadding>
                              {subIndustry.supportEcosystem.supplyChain.criticalGaps.slice(0, 2).map((item, index) => (
                                <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                  <ListItemIcon sx={{ minWidth: 28 }}>
                                    <CheckIcon color="secondary" fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={
                                      <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                        {item}
                                      </Typography>
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}

                        {subIndustry.supportEcosystem.supplyChain.localizationOpportunities && (
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                              Opportunities:
                            </Typography>
                            <List dense disablePadding>
                              {subIndustry.supportEcosystem.supplyChain.localizationOpportunities.slice(0, 2).map((opportunity, index) => (
                                <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                  <ListItemIcon sx={{ minWidth: 28 }}>
                                    <CheckIcon color="secondary" fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={
                                      <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical' }}>
                                        {opportunity.category}
                                      </Typography>
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}
                      </>
                    ) : (
                      <List dense disablePadding>
                        {['Logistics Networks', 'Raw Material Sources', 'Special Economic Zones', 'Import/Export Facilities'].map((item, index) => (
                          <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <CheckIcon color="secondary" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={item} />
                          </ListItem>
                        ))}
                      </List>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 2 }}>
                      <Typography variant="button" color="secondary.main" sx={{ display: 'flex', alignItems: 'center' }}>
                        Explore
                        <ArrowForwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                      </Typography>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Related sub-industries with improved cards */}
      <Box mt={8} mb={4}>
        <Typography
          variant="h4"
          sx={{
            mb: 4,
            fontWeight: 600,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 60,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.secondary.main
            }
          }}
        >
          Related Sub-Industries
        </Typography>

        <Grid container spacing={3}>
          {industry.subIndustries
            .filter(sub => sub.id !== subId)
            .map((relatedSub, index) => (
              <Grid item xs={12} sm={6} md={4} key={relatedSub.id}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 20px -10px rgba(0,0,0,0.2)'
                    },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <CardActionArea
                    onClick={() => handleRelatedSubIndustryClick(relatedSub.id)}
                    sx={{
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      height: '100%'
                    }}
                  >
                    <Box
                      sx={{
                        height: 8,
                        width: '100%',
                        background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.light} 50%, transparent)`
                      }}
                    />

                    <CardContent sx={{ p: 3, flexGrow: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          mb: 1.5,
                          fontWeight: 600,
                          color: theme.palette.text.primary
                        }}
                      >
                        {relatedSub.name}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {relatedSub.description}
                      </Typography>

                      <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', color: theme.palette.secondary.main }}>
                        <Typography variant="button" sx={{ mr: 0.5 }}>
                          Explore
                        </Typography>
                        <ArrowForwardIcon fontSize="small" />
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
        </Grid>
      </Box>

      {/* Related Content - Enhanced Grid */}
      <Box mt={8} mb={4}>
        <Typography
          variant="h4"
          sx={{
            mb: 4,
            fontWeight: 600,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 60,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main
            }
          }}
        >
          Related Content
        </Typography>

        <Grid container spacing={3}>
          {[
            {
              title: 'Industry Experts',
              icon: <PeopleIcon sx={{ fontSize: 48, color: 'white' }} />,
              description: 'Connect with leading professionals in this industry',
              path: `/experts?industry=${industry.id}`
            },
            {
              title: 'Investment Offers',
              icon: <MonetizationOnIcon sx={{ fontSize: 48, color: 'white' }} />,
              description: 'Explore current investment opportunities',
              path: `/marketplace`
            },
            {
              title: 'Innovation Hub',
              icon: <ScienceIcon sx={{ fontSize: 48, color: 'white' }} />,
              description: 'Discover the latest in this industry R&D',
              path: `/articles?type=innovation&industry=${industry.id}`
            },
            {
              title: 'Industry News',
              icon: <ArticleIcon sx={{ fontSize: 48, color: 'white' }} />,
              description: 'Stay updated with market trends',
              path: `/articles?type=news&industry=${industry.id}`
            }
          ].map((item, index) => (
            <Grid item xs={12} sm={6} lg={3} key={index}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
                  }
                }}
              >
                <CardActionArea
                  sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
                  onClick={() => navigate(item.path)}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: 120,
                      background: index % 2 === 0
                        ? `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.8)}, ${alpha(theme.palette.primary.main, 0.9)})`
                        : `linear-gradient(135deg, ${alpha(theme.palette.secondary.light, 0.8)}, ${alpha(theme.palette.secondary.main, 0.9)})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {item.icon}
                  </Box>

                  <CardContent sx={{ p: 3, flexGrow: 1 }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            endIcon={<ArrowForwardIcon />}
            size="large"
            sx={{
              borderRadius: 2,
              py: 1.2,
              px: 4,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)'
            }}
          >
            Explore All Content
          </Button>
        </Box>
      </Box>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 6, mb: 4 }}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<NavigateNextIcon sx={{ transform: 'rotate(180deg)' }} />}
          onClick={() => navigate(`/industry/${id}`)}
          sx={{
            borderRadius: 2,
            py: 1,
            px: 3,
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Back to {industry.name}
        </Button>

        <Button
          variant="contained"
          color="secondary"
          endIcon={<ArrowForwardIcon />}
          size="large"
          sx={{
            borderRadius: 2,
            py: 1,
            px: 3,
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)'
          }}
        >
          Explore Investment Opportunities
        </Button>
      </Box>
    </Container>
  );
};

export default SubIndustryPage;